import React from 'react';
import {
  Box,
  type BoxProps,
  Tabs as MuiTabs,
  type TabsProps as MuiTabsProps,
  styled,
  tabsClasses,
} from '@mui/material';
import { Tab, TabProps } from './Tab';
import { TabPanel, TabPanelProps } from './TabPanel';

type TabsItemProps = Omit<MuiTabsProps, 'children'> & {
  rootProps?: BoxProps;
  defaultValue?: number | string | undefined;
  items?: (TabProps & {
    id: string;
    panel?: React.ReactNode;
    panelProps?: Omit<TabPanelProps, 'tabValue' | 'value'>;
  })[];
  children?: never;
};

type TabsChildrenProps = MuiTabsProps & {
  rootProps?: BoxProps;
  defaultValue?: number | string | undefined;
  items?: never;
};

export type TabsProps = TabsChildrenProps | TabsItemProps;

export const Tabs = ({
  children,
  defaultValue,
  items,
  rootProps,
  value: controlledValue,
  onChange: onChangeControlled,
  ...restProps
}: TabsProps) => {
  const [uncontrolledValue, setUncontrolledValue] = React.useState<TabProps['value']>(defaultValue);
  const onChange: TabsProps['onChange'] = (e, newValue: TabProps['value']) => {
    onChangeControlled?.(e, newValue);
    setUncontrolledValue(() => newValue);
  };
  const value = onChangeControlled && controlledValue ? controlledValue : uncontrolledValue;
  return (
    <Box width="100%" {...rootProps}>
      <StyledTabs onChange={onChange} value={value} {...restProps}>
        {items?.map(({ id, panel, panelProps, ...rest }) => <Tab key={id} {...rest} />)}
        {children}
      </StyledTabs>
      {items?.map(
        ({ id, panel, panelProps, value: tabValue }) =>
          panel && (
            <TabPanel key={id} value={value} {...panelProps} tabValue={tabValue}>
              {panel}
            </TabPanel>
          )
      )}
    </Box>
  );
};

const StyledTabs = styled(MuiTabs)(({ theme }) => ({
  borderBottom: `0.5px solid ${theme.palette.border.primarySubtle}`,
  [`& .${tabsClasses.indicator}`]: {
    backgroundColor: theme.palette.border.brandSecondary,
    height: '1px',
  },
}));
